import React, { useState, useEffect, useCallback } from 'react';
import { AnalyticsService } from '@/services/analytics.service';
import {
  UserAnalytics,
  EngagementMetrics,
  ProductAnalytics,
  BusinessMetrics,
} from '@/models/analytics.model';

export const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'product' | 'financial'>('overview');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [engagementMetrics, setEngagementMetrics] = useState<EngagementMetrics | null>(null);
  const [productAnalytics, setProductAnalytics] = useState<ProductAnalytics | null>(null);
  const [businessMetrics, setBusinessMetrics] = useState<BusinessMetrics | null>(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate: Date;

      switch (period) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter': {
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
          break;
        }
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
      }

      const [userData, engagementData, productData, businessData] = await Promise.all([
        AnalyticsService.getUserAnalytics(startDate, now),
        AnalyticsService.getEngagementMetrics(startDate, now),
        AnalyticsService.getProductAnalytics(startDate, now),
        AnalyticsService.getBusinessMetrics(),
      ]);

      setUserAnalytics(userData);
      setEngagementMetrics(engagementData);
      setProductAnalytics(productData);
      setBusinessMetrics(businessData);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      console.error('Error details:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
      });
      // Set empty data on error to prevent UI crashes
      setUserAnalytics({
        totalUsers: 0,
        newUsers: 0,
        activeUsers: 0,
        retentionRate: 0,
        byRole: { members: 0, employers: 0, providers: 0, admins: 0 },
        growthTrend: [],
      });
      setEngagementMetrics({
        dailyActiveUsers: 0,
        weeklyActiveUsers: 0,
        monthlyActiveUsers: 0,
        averageSessionDuration: 0,
        receiptsSubmitted: 0,
        reimbursementsProcessed: 0,
        providerSearches: 0,
        featureUsage: {},
      });
      setProductAnalytics({
        receipts: {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          averageProcessingTime: 0,
          byCategory: {},
          trends: [],
        },
        reimbursements: {
          total: 0,
          totalAmount: 0,
          averageAmount: 0,
          byCategory: {},
          trends: [],
        },
        providers: {
          totalSearches: 0,
          mostSearched: [],
          byType: {},
        },
        categories: {
          mostUsed: [],
        },
      });
      setBusinessMetrics({
        totalEmployers: 0,
        activeEmployers: 0,
        totalMembers: 0,
        activeMembers: 0,
        totalProviders: 0,
        subscribedProviders: 0,
        revenue: { current: 0, previous: 0, growth: 0 },
        expenses: { current: 0, previous: 0, growth: 0 },
        netIncome: { current: 0, previous: 0, growth: 0 },
      });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics & Business Intelligence</h1>
            <p className="mt-1 text-sm text-gray-500">Comprehensive insights into platform performance</p>
          </div>
          <div className="flex space-x-2">
            {(['week', 'month', 'quarter', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {(['overview', 'users', 'product', 'financial'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && businessMetrics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Employers"
                value={businessMetrics.totalEmployers.toString()}
                icon="🏢"
                color="blue"
                subtitle={`${businessMetrics.activeEmployers} active`}
              />
              <StatCard
                label="Total Members"
                value={businessMetrics.totalMembers.toString()}
                icon="👥"
                color="green"
                subtitle={`${businessMetrics.activeMembers} active`}
              />
              <StatCard
                label="Total Providers"
                value={businessMetrics.totalProviders.toString()}
                icon="🏥"
                color="purple"
                subtitle={`${businessMetrics.subscribedProviders} subscribed`}
              />
              <StatCard
                label="Revenue Growth"
                value={formatPercent(businessMetrics.revenue.growth)}
                icon="📈"
                color={businessMetrics.revenue.growth >= 0 ? 'green' : 'red'}
                subtitle={formatCurrency(businessMetrics.revenue.current)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue</span>
                    <span className="font-semibold">{formatCurrency(businessMetrics.revenue.current)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expenses</span>
                    <span className="font-semibold">{formatCurrency(businessMetrics.expenses.current)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-3">
                    <span className="font-semibold text-gray-900">Net Income</span>
                    <span className={`font-bold text-lg ${
                      businessMetrics.netIncome.current >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(businessMetrics.netIncome.current)}
                    </span>
                  </div>
                </div>
              </div>

              {engagementMetrics && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Daily Active Users</span>
                      <span className="font-semibold">{engagementMetrics.dailyActiveUsers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weekly Active Users</span>
                      <span className="font-semibold">{engagementMetrics.weeklyActiveUsers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Active Users</span>
                      <span className="font-semibold">{engagementMetrics.monthlyActiveUsers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Receipts Submitted</span>
                      <span className="font-semibold">{engagementMetrics.receiptsSubmitted}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && userAnalytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Users"
                value={userAnalytics.totalUsers.toString()}
                icon="👥"
                color="blue"
              />
              <StatCard
                label="Active Users"
                value={userAnalytics.activeUsers.toString()}
                icon="✅"
                color="green"
              />
              <StatCard
                label="New Users"
                value={userAnalytics.newUsers.toString()}
                icon="🆕"
                color="purple"
              />
              <StatCard
                label="Retention Rate"
                value={formatPercent(userAnalytics.retentionRate)}
                icon="📊"
                color="indigo"
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Members</p>
                  <p className="text-2xl font-bold text-gray-900">{userAnalytics.byRole.members}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Employers</p>
                  <p className="text-2xl font-bold text-gray-900">{userAnalytics.byRole.employers}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Providers</p>
                  <p className="text-2xl font-bold text-gray-900">{userAnalytics.byRole.providers}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Admins</p>
                  <p className="text-2xl font-bold text-gray-900">{userAnalytics.byRole.admins}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Tab */}
        {activeTab === 'product' && productAnalytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Receipts"
                value={productAnalytics.receipts.total.toString()}
                icon="🧾"
                color="blue"
              />
              <StatCard
                label="Pending"
                value={productAnalytics.receipts.pending.toString()}
                icon="⏳"
                color="yellow"
              />
              <StatCard
                label="Approved"
                value={productAnalytics.receipts.approved.toString()}
                icon="✅"
                color="green"
              />
              <StatCard
                label="Rejected"
                value={productAnalytics.receipts.rejected.toString()}
                icon="❌"
                color="red"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Reimbursements</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total</span>
                    <span className="font-semibold">{productAnalytics.reimbursements.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount</span>
                    <span className="font-semibold">{formatCurrency(productAnalytics.reimbursements.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average Amount</span>
                    <span className="font-semibold">{formatCurrency(productAnalytics.reimbursements.averageAmount)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Used Categories</h3>
                <div className="space-y-2">
                  {productAnalytics.categories.mostUsed.slice(0, 5).map((category, idx) => (
                    <div key={category.categoryId} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{idx + 1}. {category.categoryName}</span>
                      <span className="font-semibold">{category.usageCount}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && businessMetrics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                label="Revenue"
                value={formatCurrency(businessMetrics.revenue.current)}
                icon="💰"
                color="green"
                subtitle={`${formatPercent(businessMetrics.revenue.growth)} vs previous`}
              />
              <StatCard
                label="Expenses"
                value={formatCurrency(businessMetrics.expenses.current)}
                icon="💸"
                color="red"
                subtitle={`${formatPercent(businessMetrics.expenses.growth)} vs previous`}
              />
              <StatCard
                label="Net Income"
                value={formatCurrency(businessMetrics.netIncome.current)}
                icon="📊"
                color={businessMetrics.netIncome.current >= 0 ? 'green' : 'red'}
                subtitle={`${formatPercent(businessMetrics.netIncome.growth)} vs previous`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo';
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-2xl font-bold mt-2">{value}</p>
          {subtitle && (
            <p className="text-xs opacity-75 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="text-3xl opacity-75">{icon}</div>
      </div>
    </div>
  );
};

